import styled, { css } from 'styled-components';
import { COLORS } from 'components/containers/Theme/defaults';
import { getThemeFontSize, createPropGetter } from 'utilities';
import { contrast } from 'utilities/global';

import { MINIMUM_CONTRAST } from 'consts';
import Link from 'components/widgets/Link/Link';
import { getThemeTypography, Typographies, FontWeights } from 'models';
import { subTitleOverrideStyle } from 'styles/card';

export const boxWrapperRoundness = css`
  border-radius: 1.125rem;
`;

export const CtaButtonTextStyle = css`
  font-size: 1.8rem;
  font-weight: ${FontWeights.MEDIUM};
`;

export const BaseButton = styled.button`
  position: relative;
  height: 40px;
  padding: 0 10px;
  border-radius: 4px;
  ${CtaButtonTextStyle};
  cursor: pointer;
  border: unset;
  transition: 0.2s;

  ${({ alternative, backgroundColor }) => {
    const textContrast = contrast(backgroundColor || COLORS.BLUES[3], COLORS.WHITES[1]);
    const belowMinContrast = textContrast < MINIMUM_CONTRAST;

    if (alternative) {
      return css`
        background-color: ${COLORS.WHITES[1]};
        border: 1px solid ${belowMinContrast ? COLORS.BLACKS[1] : backgroundColor};
        color: ${belowMinContrast ? COLORS.BLACKS[1] : backgroundColor};
      `;
    }

    return css`
      background-color: ${backgroundColor || COLORS.BLUES[3]};
      color: ${belowMinContrast ? COLORS.BLACKS[1] : COLORS.WHITES[1]};
    `;
  }}

  &:focus {
    outline: none;
  }

  &:hover {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }

  &:active {
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);
  }

  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.4;
      pointer-events: none;
    `}
`;

export const TextWrapper = styled.div`
  line-height: 1.5;
  text-align: center;
  color: ${COLORS.BLUES[2]};
`;

export const StyledLink = styled.span`
  white-space: nowrap;
  font-weight: 700;
  color: ${COLORS.BLUES[6]};
  cursor: pointer;

  ${({ loading, disabled }) => {
    if (!loading && !disabled) {
      return;
    }
    return css`
      pointer-events: none;
      opacity: 0.7;
    `;
  }}
`;

const submitProp = createPropGetter('submit');
const base = createPropGetter('base', submitProp);
const hover = createPropGetter('hover', submitProp);
const active = createPropGetter('active', submitProp);
export const submitButtonCustomStyleOverrideCss = css`
  width: ${base('width')};
  height: ${base('height')};
  background-color: ${base('backgroundColor')};
  color: ${base('color')};
  font-weight: ${base('fontWeight')};
  font-size: ${base('fontSize')};
  border-radius: ${base('borderRadius')};
  font-family: ${base('fontFamily')};
  box-shadow: ${base('boxShadow')};
  border: ${base('border')};
  padding: ${base('padding')};
  cursor: ${base('cursor')};
  transition: ${submitProp('transition')};

  &:hover {
    background-color: ${hover('backgroundColor')};
    color: ${hover('color')};
    font-weight: ${hover('fontWeight')};
    border-radius: ${hover('borderRadius')};
    box-shadow: ${hover('boxShadow')};
    border: ${hover('border')};
  }

  &:active {
    background-color: ${active('backgroundColor')};
    color: ${active('color')};
  }
`;

const requiredFieldsProp = createPropGetter('requiredFields');
const titleProp = createPropGetter('title', requiredFieldsProp);

export const SharedTitle = styled.div`
  font-size: ${getThemeFontSize('medium')};
  font-weight: 600;
  margin-bottom: 8px;
  color: ${titleProp('color')};

  ${({ isSdk }) =>
    isSdk &&
    css`
      margin-bottom: 0;
    `};
`;

export const Separator = styled.div`
  width: 100%;
  height: 1px;
  background: ${COLORS.GREYS[6]};
  margin: 2.4rem 0;
`;

const labelStyle = css`
  ${getThemeTypography(Typographies.TAB_ACTIVE)}
  color: ${({ theme }) => theme.colors.secondaryText};
`;

export const Label = styled.div`
  ${labelStyle};
`;

export const CardSubtitle = styled(Label)`
  ${subTitleOverrideStyle};
`;

export const CapitalizedLabel = styled.div`
  ${labelStyle};
  text-transform: capitalize;
`;

export const BoxContent = styled.div`
  border-radius: 0.8rem;
  border: solid 0.1rem ${({ theme }) => theme.colors.surface3};
  padding-top: 2rem;
  padding-bottom: 2rem;
  white-space: pre-line;
`;

export const footerStyle = css`
  font-size: 1rem;
  font-weight: 500;
  font-style: italic;
  margin-right: 0.4rem;
  white-space: nowrap;
  color: ${({ theme }) => theme.colors.grey.grey30};
`;

export const ImageContainer = styled.div`
  display: flex;
  height: 8rem;
  width: 8rem;
  max-width: 8rem;
  ${boxWrapperRoundness};
  margin-right: 2.4rem;
  overflow: hidden;

  ${({ theme }) => {
    const { image } = theme.hcp?.cart || {};
    if (image) {
      return css`
        width: ${image?.width};
        height: ${image?.height};
        max-width: ${image?.width};
      `;
    }
  }}

  img {
    width: 100%;
    height: 100%;
    object-fit: fill;
    border-radius: inherit;

    ${({ theme }) => {
      const { image } = theme.hcp?.cart || {};
      if (image) {
        return css`
          transition: ${image?.transition};
          border: ${image?.border};
          border-radius: ${image?.borderRadius};

          &:hover {
            border: ${image?.hover?.border};
            border-radius: ${image?.hover?.borderRadius};
          }

          &:focus {
            outline: ${image?.focus?.outline};
          }
        `;
      }
    }}
  }
`;

export const StyledErrorMessage = styled.div`
  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.fonts.size.extraSmall};
  font-weight: 400;
  flex: 1;
`;

export const disabledStyle = css`
  opacity: 0.4;
  pointer-events: none;
`;

export const boxRoundness = css`
  border-radius: 0.8rem;
`;

export const useAnotherPMTRoundness = css`
  border-radius: 1.5rem 0.375rem 1.5rem 1.5rem;
`;

export const ContentWrapper = styled.div`
  flex: 1;
`;

export const MobileContentWrapper = styled.div`
  ${({ theme }) => {
    const { isMobile } = theme.view || {};
    if (isMobile) {
      return css`
        display: flex;
        flex-direction: column;
        flex: 1;
      `;
    }
  }}
`;

export const StyledMobileContentWrapper = styled(MobileContentWrapper)`
  ${({ theme }) => {
    const { isMobile } = theme.view || {};
    if (isMobile) {
      return css`
        justify-content: space-between;
      `;
    }
  }}
`;

export const pageContentStyle = css`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

export const globalWrapperStyle = css`
  ${pageContentStyle};
  ${({ theme }) => theme.view.isMobile && 'height: 100%'};
`;

export const GlobalWrapper = styled.div`
  > *:not(:last-child) {
    margin-bottom: 1.5rem;
  }
`;

export const TermsPolicyLink = styled(Link)`
  padding-right: 0.3rem;
  padding-left: 0.3rem;
`;

export const CopyWrapper = styled.div`
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: 0.5rem;
`;

export const backDropShadow = ({ applyFullOpacity, transition }) => `
  background: rgba(24, 28, 34, 0.8);
  opacity: ${applyFullOpacity ? '1' : '0'} !important;
  transition: ${transition} !important;
`;
